'use client'

import BlockIcon from '@/components/icons/block'
import { Image } from '@/components/ui/image'
import { Box, Flex, type FlexProps } from '@/styled-system/jsx'
import useGameSettings from '../shared/settings/useGameSettings'

export type TowerGameTileProps = FlexProps & {
  state?: 'hidden' | 'active' | 'fetching' | 'chosen' | 'revealed'
  status?: 'win' | 'lose' | 'none'
}

export default function TowerGameTile({
  status = 'none',
  state = 'hidden',
  ...props
}: TowerGameTileProps) {
  const animationsEnabled = useGameSettings((state) => state.animationsEnabled)

  return (
    <Flex
      position='relative'
      align='center'
      justify='center'
      w='full'
      h={{ base: '32px', md: '40px' }}
      borderRadius='sm'
      transition='transform 0.2s'
      cursor='pointer'
      style={
        {
          '--duration': animationsEnabled ? '0.3s' : '0s',
          '--fetch-duration': animationsEnabled ? '0.6s' : '0s',
        } as React.CSSProperties
      }
      {...{
        hidden: {
          bg: 'neutral.200',
        },
        active: {
          bg: 'neutral.300',
          boxShadow: '0 0.3em token(colors.neutral.200)',
          transitionProperty: 'transform, background, box-shadow',
          transitionDuration: 'var(--duration)',
          _hover: {
            bg: 'neutral.400',
            boxShadow: '0 0.3em token(colors.neutral.300)',
          },
          _active: {
            bg: 'neutral.400',
            boxShadow: '0 0.15em token(colors.neutral.300)',
            transform: 'translateY(0.15em)',
          },
        },
        fetching: {
          bg: 'neutral.300',
          boxShadow: '0 0.3em token(colors.neutral.200)',
          transitionProperty: 'transform, background, box-shadow',
          transitionDuration: 'var(--duration)',
          animationName: 'tile-pulse',
          animationDuration: 'var(--fetch-duration)',
          animationDelay: 'var(--duration)',
          animationIterationCount: 'infinite',
        },
        chosen: {
          ...(status === 'lose'
            ? {
                bg: 'neutral.50',
                boxShadow: '0 0.3em #000',
              }
            : {
                bg: 'neutral.100',
                boxShadow:
                  '0 0.3em token(colors.pink.950), inset 0 0 0 0.1em token(colors.primary)',
              }),
        },
        revealed: {
          bg: 'neutral.200',
          animationName: 'tower-reveal',
          animationDuration: '1ms',
          animationDelay: '0.8s',
          animationFillMode: 'forwards',
        },
      }[state]}
      {...props}
    >
      {(state === 'chosen' && status === 'lose') || state === 'revealed' ? (
        <Box
          position='absolute'
          top='50%'
          left='50%'
          h='70%'
          opacity={0}
          transform='translate(-50%, -50%)'
          willChange='transform'
          pointerEvents='none'
          animationName='fade-in'
          animationDuration='var(--duration)'
          animationTimingFunction='cubic-bezier(0.87, 0, 0.13, 1)'
          animationFillMode='forwards'
          animationDelay={state === 'revealed' ? '0.8s' : '0s'}
        >
          <BlockIcon color='neutral.300' />
        </Box>
      ) : null}

      {(state === 'chosen' || state === 'revealed') && status === 'win' ? (
        <Box
          position='absolute'
          top='50%'
          left='50%'
          h='70%'
          opacity={0}
          transform='translate(-50%, -50%) scale(0)'
          willChange='transform'
          pointerEvents='none'
          animationName='hilo-guess'
          animationDuration='var(--duration)'
          animationTimingFunction='cubic-bezier(0.87, 0, 0.13, 1)'
          animationFillMode='forwards'
          animationDelay={state === 'revealed' ? '0.8s' : '0s'}
        >
          <Image
            alt='Gem'
            src='/assets/casino/gem.svg'
            w='full'
            h='full'
            objectFit='contain'
          />
        </Box>
      ) : null}
    </Flex>
  )
}
